/**
 * Cookie NAME constants for every anonymous/guest personalization signal,
 * split out of `@/lib/personalization/anonymous-signals.ts` into their own
 * dependency-free module.
 *
 * Why this file exists: `src/lib/cache/edge-html-cache.ts` runs on every
 * request (it bundles into the outer `src/worker.ts` wrapper) and must know
 * every one of these cookie names to decide whether a request is eligible
 * for the dynamic-tier edge cache (see `PERSONALIZATION_COOKIES` there) — a
 * cookie-carrying guest must never receive (or poison) a generic cached
 * response. Previously that list was a hand-maintained set of string
 * literals, kept "in lockstep" with this file only by a code comment — a new
 * signal cookie added here without a matching edit there would silently
 * serve stale cached HTML to personalized visitors.
 *
 * This file has ZERO imports and must stay that way — it is safe to import
 * directly from the hot-path `edge-html-cache.ts` precisely because it can
 * never drag in a heavier dependency graph (unlike `anonymous-signals.ts`,
 * which imports `@/lib/match/skills`). Add a new signal cookie's NAME
 * constant here (re-exported from `anonymous-signals.ts` for existing
 * importers) and list it in {@link ANONYMOUS_SIGNAL_COOKIE_NAMES} — the edge
 * cache then picks it up automatically, no second edit required.
 */

/** Anonymous category-browse signal — written by category page views, read
 * server-side by the feed to personalize ranking for guests. */
export const ANON_SIGNAL_COOKIE = "ld_pref_v1";

/** Sticky job-type / remote-type filter preference, captured from filter UI
 * usage (authenticated or anonymous) and restored across sessions. */
export const FILTER_SIGNAL_COOKIE = "ld_filters_v1";

/** ISO timestamp of guest's last feed view — written by FeedSeenBeacon, read
 * by RSC to SSR the new-jobs count. */
export const GUEST_LAST_SEEN_COOKIE = "ld_seen_v1";

/** sinceISO of the dismissed new-jobs banner — written by NewJobsBanner on
 * dismiss, read by RSC to skip re-rendering it. */
export const BANNER_DISMISSED_COOKIE = "ld_banner_seen";

/** Rolling list of recently-viewed jobs' skills/industry/remote-pref —
 * written client-side on job detail views, read server-side to warm-start
 * Hunt and to seed feed personalization. */
export const HUNT_INTEREST_COOKIE = "hunt_interest_v1";

/** Rolling list of recently-viewed job id/title/company — written and read
 * client-side to render the "Recently viewed" rail. Included here even
 * though today's read path is client-only: it is a guest personalization
 * signal like the others, and keeping it in lockstep with the cache-bypass
 * list is what makes a future server-rendered use of it safe by default. */
export const RECENT_JOBS_COOKIE = "ld_recent_jobs_v1";

/** Session-only dismiss flag for the repeat-search "save this search" nudge —
 * written by RepeatSearchNudge on dismiss, read by RSC to skip re-rendering
 * it for the rest of the session. Necessary UX state, not a personalization
 * signal, but still guest-readable and so must stay in the cache-bypass list
 * like BANNER_DISMISSED_COOKIE above. */
export const SEARCH_NUDGE_DISMISSED_COOKIE = "ld_search_nudge_seen";

/**
 * Every anonymous/guest personalization signal cookie NAME. Any request
 * carrying one of these must bypass the dynamic-tier edge cache (see
 * `hasPersonalizationCookie` in `@/lib/cache/edge-html-cache`) rather than
 * receive (or poison) the generic cached response.
 *
 * Keep this list exhaustive — every signal cookie constant exported from
 * `@/lib/personalization/anonymous-signals` must appear here.
 */
export const ANONYMOUS_SIGNAL_COOKIE_NAMES = [
	ANON_SIGNAL_COOKIE,
	FILTER_SIGNAL_COOKIE,
	GUEST_LAST_SEEN_COOKIE,
	BANNER_DISMISSED_COOKIE,
	HUNT_INTEREST_COOKIE,
	RECENT_JOBS_COOKIE,
	SEARCH_NUDGE_DISMISSED_COOKIE,
] as const;

/**
 * Whether a guest's cookie jar carries anything the `/jobs` feed ranks with:
 * one of the three ranking signal cookies the page's guest branch reads, or
 * PostHog's persistence cookie (`ph_<api-key>_posthog`), whose distinct id
 * keys the D1 behavioral history.
 *
 * Used by the feed client island when the server rendered the generic feed
 * (`EDGE_HTML_CACHE_GUEST_GENERIC` strips cookies before origin renders): if
 * the browser holds a signal the server never saw, the island re-fetches
 * `/api/feed` with the real cookies and swaps in the personalized ranking.
 * Takes the raw `document.cookie` / `cookie` header string.
 */
export function hasFeedRankingSignal(cookieHeader: string): boolean {
	for (const part of cookieHeader.split(";")) {
		const name = part.split("=", 1)[0]?.trim() ?? "";
		if (
			name === ANON_SIGNAL_COOKIE ||
			name === FILTER_SIGNAL_COOKIE ||
			name === HUNT_INTEREST_COOKIE ||
			(name.startsWith("ph_") && name.endsWith("_posthog"))
		) {
			return true;
		}
	}
	return false;
}
